import { getFocusableElements } from '../utils/helpers';

type QueriedElements = {
    [property: string]: HTMLElement
}

export const promoTab = {
    init: async function () {
        const $promoTabs = document.querySelectorAll('[data-ot-promo-tab]');

        (Array.from($promoTabs) as HTMLElement[]).forEach(($promoTab: HTMLElement) => {
            const promoTabId = $promoTab.getAttribute('data-ot-promo-tab');
            
            if (!promoTabId) return;

            const $triggerWrapper = document.querySelector(`[data-ot-promo-tab-trigger="${promoTabId}"]`) as HTMLElement;

            let $triggerStart = document.querySelector(`[data-ot-promo-tab-trigger-start="${promoTabId}"]`) as HTMLElement;
            let $triggerEnd = document.querySelector(`[data-ot-promo-tab-trigger-end="${promoTabId}"]`) as HTMLElement;

            // If $triggerStart/$triggerEnd each have data-ot-promo-tab-trigger-ancestor attr set to classname,
            // look for ancestors of $triggerStart/$triggerEnd that have that classname
            // and assign $triggerStart/$triggerEnd to those elements instead
            // (For cases where trigger start/end and elements to be wrapped are not adjacent, and are instead nested in separate adjacent elements)
            const $triggerStartAncestorClassName = $triggerStart?.getAttribute('data-ot-promo-tab-trigger-ancestor');
            const $triggerEndAncestorClassName = $triggerEnd?.getAttribute('data-ot-promo-tab-trigger-ancestor');

            const $triggerStartAncestor = $triggerStartAncestorClassName && $triggerStart.closest($triggerStartAncestorClassName) as HTMLElement | null;
            const $triggerEndAncestor = $triggerEndAncestorClassName && $triggerEnd.closest($triggerEndAncestorClassName) as HTMLElement | null;
            
            if ($triggerStartAncestor && $triggerEndAncestor) {
                $triggerStart = $triggerStartAncestor;
                $triggerEnd = $triggerEndAncestor;
            }

            const $trigger = (!$triggerWrapper && $triggerStart && $triggerEnd) ? 
                this.wrapElementsBetween($triggerStart, $triggerEnd, promoTabId) : 
                $triggerWrapper;

            if (!$trigger || !promoTabId) return;

            const queriedElements = { $promoTab, $trigger };

            this.createObserver(queriedElements);
        });

        document.addEventListener('keydown', (e: KeyboardEvent) => {
            // If shift-tabbing from the promo tab, move focus to the last focusable element before the promo tab trigger area
            // If tabbing from the promo tab, move focus to the first focusable element in the promo tab trigger area
            // If shift-tabbing from the first focusable element in the promo-tab trigger area, move focus to promo tab
            if (e.key !== 'Tab') return;

            const promoTabId = (e.target instanceof HTMLElement) && e.target.getAttribute('data-ot-promo-tab')
            const $promoTabTrigger = (e.target instanceof HTMLElement) ? e.target.closest('[data-ot-promo-tab-trigger]') as HTMLElement: null;
            
            if (promoTabId) {
                const $promoTabTrigger = document.querySelector(`[data-ot-promo-tab-trigger=${promoTabId}]`) as HTMLElement;
                const $triggerAreaFirstFocusableElement = $promoTabTrigger && getFocusableElements($promoTabTrigger).first;
                const $allFocusableElements = Array.from(getFocusableElements(document.body).all);
                
                if (!$triggerAreaFirstFocusableElement) return;

                const previousFocusableElementIndex = $allFocusableElements.findIndex($el => $el === $triggerAreaFirstFocusableElement) - 1;
                const $previousFocusableElement = $allFocusableElements[previousFocusableElementIndex];

                e.preventDefault();

                if (e.shiftKey) {
                    $previousFocusableElement && $previousFocusableElement.focus();
                } else {
                    $triggerAreaFirstFocusableElement.focus();
                }
            } else if ($promoTabTrigger && e.shiftKey) {
                const promoTabId = $promoTabTrigger.getAttribute('data-ot-promo-tab-trigger');
                const $promoTab = promoTabId && document.querySelector(`[data-ot-promo-tab="${promoTabId}"]`) as HTMLElement;
                const $firstFocusableElement = getFocusableElements($promoTabTrigger).first;

                if (e.target === $firstFocusableElement && $promoTab) {
                    e.preventDefault();
                    $promoTab.focus();
                }
            }
        })
    },
    createObserver(queriedElements: QueriedElements) {
        const $target = queriedElements.$promoTab;
        const observerOptions = {
            rootMargin: '-50% 0px -50% 0px',
        };
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                setTimeout(() => {
                    if (entry.isIntersecting) {
                        this.show($target);
                    } else {
                        this.hide($target);
                    }
                }, 300)
            });
        }, observerOptions);

        observer.observe(queriedElements.$trigger);
    },
    show($target: HTMLElement) {
        const toggleClass = $target.getAttribute('data-ot-toggle-class');
        toggleClass && $target.classList.remove(toggleClass);
        $target.removeAttribute('tabindex')
    },
    hide($target: HTMLElement) {
        const toggleClass = $target.getAttribute('data-ot-toggle-class');
        toggleClass && $target.classList.add(toggleClass);
        $target.tabIndex = -1;
    },
    wrapElementsBetween($start: HTMLElement, $end: HTMLElement, promoTabId: string) {
        const $parent = $start.parentNode as HTMLElement;
        const $wrapper = document.createElement('div');

        $wrapper.setAttribute('data-ot-promo-tab-trigger', promoTabId)

        let $current = $start.nextElementSibling;

        while ($current && $current !== $end) {
            const $next = $current.nextElementSibling; // Save reference before moving
            $wrapper.appendChild($current);            // Move element into wrapper
            $current = $next;
        }

        $parent.insertBefore($wrapper, $end); // Insert wrapper before end element

        $start.remove();
        $end.remove();

        return $wrapper;
    }
};
